/* eslint-disable tss-unused-classes/unused-classes */
import { Theme } from '@mui/material';
import { CSSObject } from 'tss-react';
import { makeStyles } from 'tss-react/mui';

import { COLORS } from '@/styles/tokens/colors';
import { FONT_SIZING as FONT_SIZING_EXTERNAL } from '@/styles/tokens/fonts';

import { TextEditorContext } from '../contexts/textEditor.context';

const checkboxPseudoBase = {
  content: '""',
  position: 'absolute' as const,
  left: 0,
  top: '3px',
  // top: '0rem',
  width: '1rem',
  height: '1rem',
  border: `1px solid ${COLORS.NEUTRAL_GRAY[400]}`,
  borderRadius: '0.2rem',
};

const checkmarkStyles = {
  content: '"x"',
  position: 'absolute' as const,
  left: '4px',
  top: '0px',
  fontSize: '0.9rem',
  color: '#FFFFFF',
};

// Shared table styles for both user-generated and context content themes.
// When editing, we want to show a delineation around the cells and the header,
// so users can see the structure of the table and navigate between cells
const getSharedTableStyles = (theme: Theme, isEditing: boolean) => {
  const borderStyle = `1px solid ${COLORS.NEUTRAL_GRAY[200]}`;
  return {
    table: {
      borderCollapse: 'collapse' as const,
      borderSpacing: 0,
      maxWidth: '100%',
      overflow: 'hidden',
      tableLayout: 'fixed' as const,
      width: '100%',
      marginBottom: theme.spacing(1),
    },
    tableCell: {
      border: isEditing ? borderStyle : 'none',
      borderTop: borderStyle,
      padding: '12px 8px',
      verticalAlign: 'text-top' as const,
      textAlign: 'left' as const,
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      fontWeight: 500,
      color: COLORS.NEUTRAL_GRAY[700],
      position: 'relative' as const,
      outline: 'none',
      // Apply the cell defaults to block elements only. Inline spans must
      // inherit from their block so heading and text formatting stays visible.
      // Inline poppers must keep their own typography and spacing.
      '& :is(p, li, div):not(:where(.MuiPopper-root, .MuiPopper-root *))': {
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        fontWeight: 500,
        color: COLORS.NEUTRAL_GRAY[700],
        paddingBottom: '0',
      },
    },
    tableCellHeader: {
      border: isEditing ? borderStyle : 'none',
      borderBottom: !isEditing
        ? `1px solid ${COLORS.NEUTRAL_GRAY[200]}`
        : undefined,
      backgroundColor: isEditing ? COLORS.NEUTRAL_GRAY[50] : undefined,
      padding: theme.spacing(1),
      verticalAlign: 'baseline' as const,
      textAlign: 'left' as const,
      fontSize: '0.625rem',
      lineHeight: '1rem',
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.125rem',
      color: COLORS.NEUTRAL_GRAY[500],
      // Apply the cell defaults to block elements only. Inline spans must
      // inherit from their block so heading and text formatting stays visible.
      // Inline poppers must keep their own typography and spacing.
      '& :is(p, li, div):not(:where(.MuiPopper-root, .MuiPopper-root *))': {
        fontSize: '0.625rem',
        lineHeight: '1rem',
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.125rem',
        color: COLORS.NEUTRAL_GRAY[500],
        paddingBottom: '0',
      },
    },
    tableCellSelected: {
      caretColor: 'transparent',
      '&::after': {
        position: 'absolute' as const,
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        backgroundColor: COLORS.BLUE[50],
        mixBlendMode: 'multiply' as const,
        content: '""',
        pointerEvents: 'none' as const,
      },
    },
    tableSelection: {
      '& *::selection': {
        backgroundColor: 'transparent',
      },
    },
  };
};

function getUserGeneratedTheme(
  theme: Theme,
  isEditing: boolean,
  isPresentation: boolean,
  bodyTextColor?: string | null,
  bodyTextAlignment?: 'left' | 'center' | 'right' | null
) {
  // We're redefining this font sizing ramp specific for this markdown component so that users can
  // differentiate the hierarchy of headings, but without having a large number of very large h1
  // elements on the list and without impacting the global font sizing in the app.
  // https://withluminary.slack.com/archives/C05HVB27NFJ/p1701815624999279
  const FONT_SIZING: typeof FONT_SIZING_EXTERNAL = {
    xs: {
      fontSize: '0.7rem',
      lineHeight: '0.875rem',
    },
    sm: {
      fontSize: '0.75rem',
      lineHeight: '1rem',
    },
    md: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    },
    lg: {
      fontSize: isPresentation ? '1.0625rem' : '1rem',
      lineHeight: '1.5rem',
    },
    xl: {
      fontSize: '1.125rem',
      lineHeight: '1.75rem',
    },
    xxl: {
      fontSize: '1.25rem',
      lineHeight: '1.875rem',
    },
    xxxl: {
      fontSize: '1.125rem',
      lineHeight: '1.75rem',
    },
  } as const;

  const blockquoteStyle = {
    margin: 0,
    // these need to be set as margins so the border is also shifted
    marginLeft: '20px',
    marginBottom: '8px',
    borderLeftColor: COLORS.NEUTRAL_GRAY[300],
    borderLeftWidth: '4px',
    borderLeftStyle: 'solid' as const,
    paddingLeft: '16px',
    paddingBottom: '0',
    ...FONT_SIZING.md,
  };

  const codeStyle = {
    backgroundColor: COLORS.NEUTRAL_GRAY[100],
    padding: '2px 4px',
    borderRadius: '4px',
  };

  const sharedStyles = {
    margin: 0,
    paddingBottom: theme.spacing(0.5),
    // Tenant-branded body alignment, baked into the class alongside
    // bodyTextColor. Omitted when unset so the default (left) is kept.
    ...(bodyTextAlignment && { textAlign: bodyTextAlignment }),
  };

  const ul = {
    padding: 0,
    listStylePosition: 'outside' as const,
    ...sharedStyles,
  };

  const ol1 = {
    padding: 0,
    listStylePosition: 'outside' as const,
    ...sharedStyles,
  };

  const ol2 = {
    padding: 0,
    margin: 0,
    listStyleType: 'upper-alpha' as const,
    listStylePosition: 'outside' as const,
  };

  const ol3 = {
    padding: 0,
    margin: 0,
    listStyleType: 'lower-alpha' as const,
    listStylePosition: 'outside' as const,
  };

  const ol4 = {
    padding: 0,
    margin: 0,
    listStyleType: 'upper-roman' as const,
    listStylePosition: 'outside' as const,
  };

  const ol5 = {
    padding: 0,
    margin: 0,
    listStyleType: 'lower-roman' as const,
    listStylePosition: 'outside' as const,
  };

  const checkboxBaseStyles = {
    position: 'relative' as const,
    listStyleType: 'none' as const,
    marginLeft: 0,
    paddingLeft: '1.75rem',
  };

  // When provided, override the default text colors so tenant-branded
  // body text wins at the Lexical theme layer — avoiding a cascade
  // battle with the parent `sx` rule (which shares specificity with the
  // Lexical class colors and resolves by source order).
  const headingColor = bodyTextColor ?? COLORS.PRIMITIVES.BLACK;
  const bodyColor = bodyTextColor ?? undefined;

  return {
    h1: {
      ...(theme.typography['title-xl'] as CSSObject),
      ...FONT_SIZING.xxl,
      color: headingColor,
      ...sharedStyles,
    },
    h2: {
      ...(theme.typography['title-xl'] as CSSObject),
      ...FONT_SIZING.xl,
      color: headingColor,
      ...sharedStyles,
    },
    h3: {
      ...(theme.typography['title-l'] as CSSObject),
      ...FONT_SIZING.lg,
      color: headingColor,
      ...sharedStyles,
    },
    h4: {
      ...(theme.typography['title-l'] as CSSObject),
      ...FONT_SIZING.md,
      color: headingColor,
      ...sharedStyles,
    },
    h5: {
      ...(theme.typography['title-s'] as CSSObject),
      ...FONT_SIZING.sm,
      color: headingColor,
      ...sharedStyles,
    },
    h6: {
      ...(theme.typography['title-xs'] as CSSObject),
      ...FONT_SIZING.xs,
      color: headingColor,
      ...sharedStyles,
    },
    list: {
      listitem: {
        ...(theme.typography['body-s'] as CSSObject),
        ...(bodyColor != null && { color: bodyColor }),
        ...(bodyTextAlignment && { textAlign: bodyTextAlignment }),
        margin: `0 ${theme.spacing(3)}`,
        '&::marker': {
          fontVariantNumeric: 'normal',
        },
      },
      listitemChecked: {
        ...checkboxBaseStyles,
        '&::before': {
          ...checkboxPseudoBase,
          background: theme.palette.primary.main,
        },
        '&::after': checkmarkStyles,
      },
      listitemUnchecked: {
        ...checkboxBaseStyles,
        '&::before': {
          ...checkboxPseudoBase,
          background: theme.palette.background.paper,
        },
      },
      nested: {
        listitem: {
          listStyleType: 'none',
          'ul:not([data-lexical-list-type="check"]) &::before': {
            display: 'none',
          },
          'ul:not([data-lexical-list-type="check"]) &::after': {
            display: 'none',
          },
        },
      },
      olDepth: [ol1, ol2, ol3, ol4, ol5],
      ul,
    },
    p: {
      ...(theme.typography['body-s'] as CSSObject),
      ...(bodyColor != null && { color: bodyColor }),
      ...sharedStyles,
      // if the paragraph only contains a br tag (e.g. is just a newline),
      // we want to remove the margin and padding bottom so that the paragraph
      // is slightly smaller and is more visually appealing.
      '&:has(br:only-child)': {
        margin: 0,
        paddingBottom: 0,
        lineHeight: 1,
      },
      '&:has(span:empty:only-child)': {
        margin: 0,
        paddingBottom: 0,
        lineHeight: 1,
      },
    },
    blockquote: blockquoteStyle,
    bold: {
      fontWeight: 700,
    },
    code: codeStyle,
    italic: {
      fontStyle: 'italic',
    },
    strikethrough: {
      textDecoration: 'line-through',
    },
    subscript: {
      verticalAlign: 'sub',
    },
    superscript: {
      verticalAlign: 'super',
    },
    underline: {
      textDecoration: 'underline',
    },
    underlineStrikethrough: {
      textDecoration: 'underline line-through',
    },
    // Horizontal rule. Tinted with the tenant's primary brand color (resolves
    // to the customer brand under CustomerThemeProvider, Luminary elsewhere).
    // Inert unless the HorizontalRuleNode is registered (slide editor only).
    hr: {
      border: 'none',
      borderTop: `1px solid ${theme.palette.primary.main}`,
      opacity: 0.25,
      margin: theme.spacing(1.5, 0),
    },
    ...getSharedTableStyles(theme, isEditing),
  } as const;
}

function getContextContentTheme(theme: Theme, isEditing: boolean) {
  const blockquoteStyle = {
    margin: 0,
    marginLeft: '20px',
    borderLeftColor: COLORS.NEUTRAL_GRAY[300],
    borderLeftWidth: '4px',
    borderLeftStyle: 'solid' as const,
    paddingLeft: '16px',
    '& *': {
      paddingBottom: '0',
      ...FONT_SIZING_EXTERNAL.md,
    },
  };

  const codeStyle = {
    backgroundColor: COLORS.NEUTRAL_GRAY[100],
    padding: '2px 4px',
    borderRadius: '4px',
  };

  const sharedStyles = {
    margin: 0,
    paddingBottom: theme.spacing(0.5),
  };

  const ul = {
    listStyle: 'none' as const,
    padding: 0,
    listStylePosition: 'outside' as const,
    paddingInlineStart: '20px',
    '&[data-lexical-list-type="check"]': {
      paddingInlineStart: 0,
    },
    ...sharedStyles,
  };

  const ol1 = {
    padding: 0,
    listStylePosition: 'outside' as const,
    ...sharedStyles,
  };

  const ol2 = {
    padding: 0,
    margin: 0,
    listStyleType: 'upper-alpha' as const,
    listStylePosition: 'outside' as const,
  };

  const ol3 = {
    padding: 0,
    margin: 0,
    listStyleType: 'lower-alpha' as const,
    listStylePosition: 'outside' as const,
  };

  const ol4 = {
    padding: 0,
    margin: 0,
    listStyleType: 'upper-roman' as const,
    listStylePosition: 'outside' as const,
  };

  const ol5 = {
    padding: 0,
    margin: 0,
    listStyleType: 'lower-roman' as const,
    listStylePosition: 'outside' as const,
  };

  const checkboxBaseStyles = {
    position: 'relative' as const,
    listStyleType: 'none' as const,
    marginLeft: 0,
    paddingLeft: '1.75rem',
    paddingBottom: '16px',
  };

  return {
    h1: {
      ...(theme.typography['title-xl'] as CSSObject),

      ...sharedStyles,
    },
    h2: {
      ...(theme.typography['title-xl'] as CSSObject),

      ...sharedStyles,
    },
    h3: {
      ...(theme.typography['title-l'] as CSSObject),

      ...sharedStyles,
    },
    h4: {
      ...(theme.typography['title-l'] as CSSObject),

      ...sharedStyles,
    },
    h5: {
      ...(theme.typography['title-s'] as CSSObject),
      ...sharedStyles,
    },
    h6: {
      ...(theme.typography['title-xs'] as CSSObject),

      ...sharedStyles,
    },
    list: {
      listitem: {
        ...(theme.typography['body-s'] as CSSObject),
        margin: 0,
        padding: 0,
        paddingLeft: '10px',
        listStyleImage: `url('data:image/svg+xml,<svg transform-origin="center" width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="8" height="8" rx="2" fill="%23FF594E"/></svg>');`,
        paddingBottom: '4px',

        '&::marker': {
          fontSize: '1.5em',
          lineHeight: '0.1em',
        },
      },
      listitemChecked: {
        ...checkboxBaseStyles,
        '&::before': {
          ...checkboxPseudoBase,
          background: theme.palette.primary.main,
        },
        '&::after': checkmarkStyles,
      },
      listitemUnchecked: {
        ...checkboxBaseStyles,
        '&::before': {
          ...checkboxPseudoBase,
          background: theme.palette.background.paper,
        },
      },
      nested: {
        listitem: {
          listStyleType: 'none',
          'ul:not([data-lexical-list-type="check"]) &::before': {
            display: 'none',
          },
          'ul:not([data-lexical-list-type="check"]) &::after': {
            display: 'none',
          },
        },
      },
      olDepth: [ol1, ol2, ol3, ol4, ol5],
      ul,
    },
    p: {
      ...(theme.typography['body-s'] as CSSObject),
      ...sharedStyles,
      // if the paragraph only contains a br or an empty span tag (e.g. is just a newline),
      // we want to remove the margin and padding bottom so that the paragraph
      // is slightly smaller and is more visually appealing.
      '&:has(br:only-child)': {
        margin: 0,
        paddingBottom: 0,
        lineHeight: 1,
      },
      '&:has(span:empty:only-child)': {
        margin: 0,
        paddingBottom: 0,
        lineHeight: 1,
      },
    },
    blockquote: blockquoteStyle,
    bold: {
      fontWeight: 600,
    },
    code: codeStyle,
    italic: {
      fontStyle: 'italic',
    },
    strikethrough: {
      textDecoration: 'line-through',
    },
    subscript: {
      verticalAlign: 'sub',
    },
    superscript: {
      verticalAlign: 'super',
    },
    underline: {
      textDecoration: 'underline',
    },
    underlineStrikethrough: {
      textDecoration: 'underline line-through',
    },
    hr: {
      border: 'none',
      borderTop: `1px solid ${theme.palette.primary.main}`,
      opacity: 0.25,
      margin: theme.spacing(1.5, 0),
    },
    ...getSharedTableStyles(theme, isEditing),
  } as const;
}

export const useTextEditorClasses = makeStyles<{
  textEditorTheme: TextEditorContext['theme'];
  isEditing: boolean;
  bodyTextColor?: string | null;
  bodyTextAlignment?: 'left' | 'center' | 'right' | null;
}>()((
  theme,
  { textEditorTheme, isEditing, bodyTextColor, bodyTextAlignment }
) => {
  const isContext = textEditorTheme === 'contextContent';
  const t = isContext
    ? getContextContentTheme(theme, isEditing)
    : getUserGeneratedTheme(
        theme,
        isEditing,
        textEditorTheme === 'presentationContent',
        bodyTextColor,
        bodyTextAlignment
      );

  return {
    h1: t.h1,
    h2: t.h2,
    h3: t.h3,
    h4: t.h4,
    h5: t.h5,
    h6: t.h6,
    p: t.p,
    blockquote: t.blockquote,
    bold: t.bold,
    code: t.code,
    italic: t.italic,
    strikethrough: t.strikethrough,
    subscript: t.subscript,
    superscript: t.superscript,
    underline: t.underline,
    underlineStrikethrough: t.underlineStrikethrough,
    hr: t.hr,
    listUl: t.list.ul,
    listListitem: t.list.listitem,
    listListitemChecked: t.list.listitemChecked,
    listListitemUnchecked: t.list.listitemUnchecked,
    listNestedListitem: t.list.nested.listitem,
    listOlDepth1: t.list.olDepth[0],
    table: t.table,
    tableCell: t.tableCell,
    tableCellHeader: t.tableCellHeader,
    tableCellSelected: t.tableCellSelected,
    tableSelection: t.tableSelection,
    listOlDepth2: t.list.olDepth[1],
    listOlDepth3: t.list.olDepth[2],
    listOlDepth4: t.list.olDepth[3],
    listOlDepth5: t.list.olDepth[4],
    'mermaid-block': {
      display: 'block',
      overflow: 'auto',
      maxWidth: '100%',
      marginBottom: theme.spacing(1),
    },
  };
});
